import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";

const features = [
  {
    icon: "ri-film-line",
    title: "海量影视资源",
    desc: "覆盖动作、科幻、剧情、悬疑等众多题材的电影与电视剧，最新上映与经典佳片一应俱全。",
  },
  {
    icon: "ri-filter-3-line",
    title: "多维度筛选",
    desc: "按类型、年份、地区、季数等条件精准筛选，并支持按更新时间、热度、评分排序。",
  },
  {
    icon: "ri-search-2-line",
    title: "快速全站搜索",
    desc: "输入片名、演员或导演关键词，即刻呈现匹配结果，让找片更高效。",
  },
  {
    icon: "ri-smartphone-line",
    title: "全设备适配",
    desc: "无论电脑还是手机，界面均可自适应，观影信息浏览体验流畅自然。",
  },
];

const faqs = [
  {
    q: "西瓜影院可以免费观看影片吗？",
    a: "本站为影视内容展示与信息查询平台，用于产品演示与学习，当前不提供真实的在线播放、下载与注册登录功能。",
  },
  {
    q: "收藏功能会一直保存吗？",
    a: "收藏为前端演示功能，仅保存在你当前使用的浏览器中，不会上传到服务器，清除浏览器数据后收藏记录可能会丢失。",
  },
  {
    q: "影片信息为什么与某些平台不同？",
    a: "站内所有影片信息均为演示用虚构内容，仅用于展示页面结构与交互效果，不代表任何真实影视作品。",
  },
];

export default function About() {
  useSEO({
    title: "西瓜影院 - 关于我们",
    description:
      "西瓜影院是一个专注影视内容展示与信息查询的门户平台，致力于为影视爱好者提供丰富、清晰的电影与电视剧资源。",
  });
  return (
    <div className="mx-auto px-4 py-8 md:px-8 md:py-12">
      <nav aria-label="面包屑" className="mb-5 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-400">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
        <span className="text-foreground-200">关于我们</span>
      </nav>

      {/* Intro */}
      <section className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-10">
        <span className="inline-flex items-center gap-1 rounded-full bg-primary-500 px-3 py-1 text-xs font-700 text-background-950">
          <i className="ri-movie-2-fill text-sm" aria-hidden="true"></i>
          关于西瓜影院
        </span>
        <h1 className="mt-4 font-heading text-2xl font-700 leading-tight text-foreground-950 md:text-4xl">
          让每一次观影选择都更轻松
        </h1>
        <p className="mt-4 max-w-4xl text-sm leading-7 text-foreground-400 md:text-base">
          西瓜影院是一个专注影视内容展示与信息查询的门户平台，致力于为影视爱好者提供丰富、清晰的电影与电视剧资源。
          我们以现代简洁的界面、鲜明的内容分层和便捷的检索方式，帮助用户快速了解影片信息、发现优质作品，打造便捷高效的观影信息体验。
        </p>
      </section>

      {/* Features */}
      <section className="mt-10">
        <h2 className="mb-5 flex items-center gap-2 font-heading text-xl font-700 text-foreground-950 md:text-2xl">
          <span className="flex h-5 w-1 rounded-full bg-accent-500" />
          平台特色
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {features.map((f) => (
            <div key={f.title} className="rounded-lg border border-background-200 bg-background-100 p-5">
              <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-500/15 text-primary-400">
                <i className={`${f.icon} text-xl`} aria-hidden="true"></i>
              </span>
              <h3 className="mt-4 font-700 text-foreground-950">{f.title}</h3>
              <p className="mt-2 text-sm leading-6 text-foreground-400">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Disclaimer & copyright */}
      <section className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-lg border border-background-200 bg-background-100 p-6">
          <h2 className="flex items-center gap-2 font-heading text-lg font-700 text-foreground-950">
            <i className="ri-shield-check-line text-accent-400" aria-hidden="true"></i>
            版权信息
          </h2>
          <p className="mt-3 text-sm leading-7 text-foreground-400">
            本站为演示性质的影视内容站，站内所展示的影片名称、简介、演职员等均为虚构示例，不涉及任何真实作品的版权。
            网站结构与界面设计仅用于产品演示与学习交流，请勿用于商业用途。
          </p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-100 p-6">
          <h2 className="flex items-center gap-2 font-heading text-lg font-700 text-foreground-950">
            <i className="ri-information-line text-accent-400" aria-hidden="true"></i>
            免责声明
          </h2>
          <p className="mt-3 text-sm leading-7 text-foreground-400">
            本平台不提供任何影片的在线播放与下载服务，页面内“立即播放”等按钮均为演示交互，不会跳转至任何资源站点。
            用户在使用本站过程中产生的任何行为，均与本站演示内容无关。
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="mt-10">
        <h2 className="mb-5 flex items-center gap-2 font-heading text-xl font-700 text-foreground-950 md:text-2xl">
          <span className="flex h-5 w-1 rounded-full bg-primary-500" />
          常见问题
        </h2>
        <div className="flex flex-col gap-3">
          {faqs.map((f) => (
            <div key={f.q} className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h3 className="flex items-start gap-2 font-700 text-foreground-950">
                <i className="ri-question-line mt-0.5 text-primary-400" aria-hidden="true"></i>
                {f.q}
              </h3>
              <p className="mt-2 pl-6 text-sm leading-7 text-foreground-400">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Contact (demo) */}
      <section className="mt-10 rounded-lg border border-primary-500/40 bg-primary-500/10 p-6 md:p-8">
        <h2 className="flex items-center gap-2 font-heading text-lg font-700 text-foreground-950">
          <i className="ri-chat-smile-2-line text-primary-400" aria-hidden="true"></i>
          联系我们与建议
        </h2>
        <p className="mt-3 max-w-3xl text-sm leading-7 text-foreground-300">
          如果你对西瓜影院的功能、界面或内容有任何建议，欢迎通过页面反馈。请注意：本页为演示内容，不含真实联系方式，
          提交的建议仅用于展示交互，不会被实际收集。
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <Link
            to="/search"
            className="inline-flex h-10 items-center gap-2 rounded-md bg-primary-500 px-5 text-sm font-500 text-background-950 transition-colors hover:bg-primary-600"
          >
            <i className="ri-search-line text-base" aria-hidden="true"></i>
            去搜索影片
          </Link>
          <Link
            to="/"
            className="inline-flex h-10 items-center gap-2 rounded-md border border-background-300 px-5 text-sm font-500 text-foreground-300 transition-colors hover:border-primary-500 hover:text-primary-400"
          >
            返回首页
          </Link>
        </div>
      </section>
    </div>
  );
}